import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PiSlidersHorizontal, PiXBold } from "react-icons/pi";
import { BiFilterAlt } from "react-icons/bi";
import { FiLayers } from 'react-icons/fi';
import DataLayersDrawer from './DataLayersDrawer';
import { useParcels } from '../../contexts/ParcelContext.jsx';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/chargerApiConfig';
import { logger } from '../../utils/logger';
import { Notification } from '../pro_calculator/general/Notification';
import { LoadingSpinner } from '../general/LoadingSpinner.jsx';

// Helper function to get numeric value from LADWP capacity range string for comparison
const getCapacityNumericValue = (capacityStr) => {
    if (!capacityStr || capacityStr === 'N/A' || capacityStr === 'NO CAPACITY') return -1;
    if (typeof capacityStr === 'number') return capacityStr;
    
    // Handle ">7500" case
    if (capacityStr.startsWith('>')) {
        return 7500;
    }
    
    // Handle range like "3000-3500" - use upper bound for comparison
    const match = capacityStr.match(/(\d+)-(\d+)/);
    if (match) {
        return parseInt(match[2]); // Return upper bound
    }
    
    // Try to parse as number
    const num = parseFloat(capacityStr);
    return isNaN(num) ? -1 : num;
};

const getMaxNearbyCapacity = (parcel) => {
    if (parcel?.nearby_circuits && Array.isArray(parcel.nearby_circuits) && parcel.nearby_circuits.length > 0) {
        const capacities = parcel.nearby_circuits
            .map((circuit) => Number(circuit.capacity_kw ?? circuit.capacity ?? circuit.capacity_kw_range))
            .filter((value) => Number.isFinite(value));
        if (capacities.length > 0) {
            return Math.max(...capacities);
        }
    }

    if (parcel?.nearby_circuits_ladwp && Array.isArray(parcel.nearby_circuits_ladwp) && parcel.nearby_circuits_ladwp.length > 0) {
        const sortedCircuits = [...parcel.nearby_circuits_ladwp].sort((a, b) => {
            const aVal = getCapacityNumericValue(a.capacity_kw);
            const bVal = getCapacityNumericValue(b.capacity_kw);
            return bVal - aVal;
        });
        const bestCircuit = sortedCircuits[0];
        const numericValue = getCapacityNumericValue(bestCircuit?.capacity_kw);
        if (numericValue >= 0) {
            return numericValue;
        }
    }

    return null;
};

const getFormattedMaxCapacity = (parcel) => {
    const capacity = getMaxNearbyCapacity(parcel);
    if (capacity === null) {
        return 'N/A';
    }
    return `${Math.round(capacity).toLocaleString()} kW`;
};

// Custom CSS for slider styling
const sliderStyles = `
    .weight-slider {
        -webkit-appearance: none;
        appearance: none;
        height: 8px;
        border-radius: 4px;
        outline: none;
        cursor: pointer;
        transition: opacity 0.2s;
    }
    
    .weight-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #4F46E5;
        cursor: grab;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        transition: all 0.2s;
    }
    
    .weight-slider::-webkit-slider-thumb:hover {
        background: #3730A3;
        box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        transform: scale(1.1);
    }
    
    .weight-slider::-webkit-slider-thumb:active {
        cursor: grabbing;
        transform: scale(1.2);
    }
    
    .weight-slider::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #4F46E5;
        cursor: grab;
        border: none;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        transition: all 0.2s;
    }
    
    .weight-slider::-moz-range-thumb:hover {
        background: #3730A3;
        box-shadow: 0 4px 8px rgba(0,0,0,0.3);
        transform: scale(1.1);
    }
    
    .weight-slider::-moz-range-thumb:active {
        cursor: grabbing;
        transform: scale(1.2);
    }
`;

const SearchFilters = ({
    onSearch,
    searchFilters,
    isFiltering,
    setShowWeightedSearch,
    setShowFilters,
    onShowDataLayers,
    dataLayerCount = 0
}) => {
    const [localFilters, setLocalFilters] = useState(searchFilters);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleFilterChange = (key, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSearch = () => {
        onSearch(localFilters);
        setShowFilters(false);
    };

    const handleClear = () => {
        const emptyFilters = {
            address: '',
            zipCode: '',
            city: '',
            minLandValue: '',
            maxLandValue: '',
            minArea: '',
            maxArea: ''
        };
        setLocalFilters(emptyFilters);
        onSearch(emptyFilters);
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">Search for an Industrial Land Parcel</h3>
                {/* <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                    {isExpanded ? 'Less Filters' : 'More Filters'}
                </button> */}
            </div>

            <div className="space-y-3">
                {/* Always visible filters */}
                <div className="w-full flex flex-col gap-3">
                    <input
                        type="text"
                        placeholder="Address"
                        value={localFilters.address}
                        onChange={(e) => handleFilterChange('address', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 pt-2 flex-wrap">
                    <button
                        onClick={handleSearch}
                        disabled={isFiltering || !localFilters.address || localFilters.address.trim() === ''}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isFiltering ? 'Searching...' : 'Search'}
                    </button>
                    <button
                        onClick={handleClear}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
                    >
                        Clear
                    </button>
                    <button
                        onClick={onShowDataLayers}
                        disabled={!onShowDataLayers}
                        className={`px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium flex items-center gap-2 ${
                            onShowDataLayers ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
                        }`}
                    >
                        <FiLayers className="w-4 h-4" />
                        Data Layers
                        {dataLayerCount > 0 && (
                            <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                                {dataLayerCount}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setShowFilters(true)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
                    >
                        <BiFilterAlt className="w-4 h-4" />
                        Filters
                    </button>
                    <button
                        onClick={() => setShowWeightedSearch(true)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                        <PiSlidersHorizontal className="w-4 h-4" />
                        Search Preferences
                    </button>
                </div>
            </div>
        </div>
    );
};

const LandParcelCard = forwardRef(({ parcel, onToggle, selected, highlighted, onHighlight, score }, ref) => {
    return (
        <div
            ref={ref}
            className={`bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-all duration-300 border ${
                // highlighted 
                //     ? 'border-purple-500 ring-2 ring-purple-200 bg-purple-50 shadow-lg' 
                    selected 
                        ? 'border-blue-500 ring-2 ring-blue-200 shadow-lg' 
                        : 'border-gray-200'

            }`}
            onClick={() => {
                onToggle?.(parcel);
                onHighlight?.(parcel);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { 
                if (e.key === 'Enter' || e.key === ' ') {
                    onToggle?.(parcel);
                    onHighlight?.(parcel);
                }
            }}
        >
            <div className="space-y-2">
                <div className="flex justify-between items-start">
                    <h4 className="font-semibold text-gray-800 text-sm truncate flex-1">
                        {parcel.Address || 'No address available'}
                    </h4>
                    <div className="flex flex-col items-end">
                        {/* <span className="text-xs font-medium text-purple-600 mb-1">
                            Score: {score ?? 'N/A'}
                        </span> */}
                        <span className={`text-xs ${selected ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                            {selected ? 'Selected' : `ID: ${parcel.id}`}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                        <span className="font-medium">APN: </span> 
                        <span className="text-blue-600 font-semibold">{parcel.APN || 'N/A'}</span>
                    </div>
                    <div>
                        <span className="font-medium">ZIP:</span> {parcel.Zip5 || parcel.Zip || 'N/A'}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                        <span className="font-medium">Land Value: </span>
                        <span className="text-green-600 font-semibold">
                            ${parseInt(parcel.Land_Value || 0).toLocaleString()}
                        </span>
                    </div>
                    <div>
                        <span className="font-medium">Area: </span>
                        <span className="text-blue-600 font-semibold">
                            {Math.round(parcel.Area || 0).toLocaleString()} sq ft
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                        <span className="font-medium">County:</span> {parcel.County_par || 'N/A'}
                    </div>
                    <div>
                        <span className="font-medium">Zone:</span> {parcel.zone_cla_1 || 'N/A'}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                        <span className="font-medium">Utility service provider: </span> {
                            parcel.utility_region_acronym || 'N/A'
                        }
                    </div>
                    <div>
                        <span className="font-medium">Max nearby capacity: </span>
                        <span className="text-blue-600 font-semibold">
                            {getFormattedMaxCapacity(parcel)}
                        </span>
                    </div>
                </div>


                {/* {parcel.zone_Juris && (
                    <div className="text-xs text-gray-500 border-t pt-2">
                        <span className="font-medium">Jurisdiction:</span> {parcel.zone_Juris}
                    </div>
                )} */}
            </div>
        </div>
    );
});

LandParcelCard.displayName = 'LandParcelCard';

const SmallLoadingSpinner = () => (
    <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading...</span>
    </div>
);

export const LandParcelSuggestionPanel = ({
    parcels = [],
    loading = false,
    hasMoreData = false,
    totalCount = 0,
    mapParcelCount = 0,
    currentPage = 1,
    pageSize = 100,
    onLoadMore,
    onSearch,
    searchFilters = {},
    isFiltering = false,
    isLoadingMore = false,
    highlightedParcelId = null,
    mapRef = null,
    dataLayerGroups = [],
    dataLayerCount = 0
}) => {
    const parcelRefs = useRef({});
    const { selectedParcels, setSelectedParcels, isParcelSelected, clearSelectedParcels, updateHighlightedParcel, noteParcelEngagement } = useParcels();
    const [showWeightedSearch, setShowWeightedSearch] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [notification, setNotification] = useState(null);
    const [preferenceSearchLoading, setPreferenceSearchLoading] = useState(false);
    const navigate = useNavigate();
    const [isDataLayersOpen, setIsDataLayersOpen] = useState(false);
    const panelPageSize = pageSize || 20;
    const [panelPage, setPanelPage] = useState(1);
    const [pendingPanelPage, setPendingPanelPage] = useState(null);
    const [isPageLoading, setIsPageLoading] = useState(false);
    
    // Local state for parcel selection in this panel
    const [localSelectedParcels, setLocalSelectedParcels] = useState([]);

    // Helper function to get parcel key (same as in ParcelContext)
    const getParcelKey = useCallback((parcel) => {
        if (!parcel) return undefined;
        return parcel.id ?? `${parcel.APN ?? ""}-${parcel.Zip5 ?? parcel.Zip ?? ""}-${parcel.Address ?? ""}`;
    }, []);

    // Local handlers for parcel selection in this panel
    const toggleLocalParcelSelection = useCallback((parcel) => {
        const parcelKey = getParcelKey(parcel);
        if (!parcelKey) return;
        
        setLocalSelectedParcels(prev => {
            const exists = prev.some(p => getParcelKey(p) === parcelKey);
            if (exists) {
                return prev.filter(p => getParcelKey(p) !== parcelKey);
            } else {
                return [...prev, parcel];
            }
        });
    }, [getParcelKey]);

    const isLocallySelected = useCallback((parcel) => {
        const parcelKey = getParcelKey(parcel);
        return localSelectedParcels.some(p => getParcelKey(p) === parcelKey);
    }, [localSelectedParcels, getParcelKey]);

    const clearLocalSelection = useCallback(() => {
        setLocalSelectedParcels([]);
    }, []);

    const handleAddToTable = useCallback(() => {
        // Add local selections to the global context
        const existingKeys = new Set(selectedParcels.map(p => getParcelKey(p)));
        const newParcels = localSelectedParcels.filter(p => !existingKeys.has(getParcelKey(p)));
        setSelectedParcels(prev => {
            const keys = new Set(prev.map(p => getParcelKey(p)));
            return [...prev, ...newParcels.filter(p => !keys.has(getParcelKey(p)))];
        });
        newParcels.forEach((parcel) => noteParcelEngagement?.(parcel, "added_to_table"));
        // Navigate to compare view
        navigate('/charger/compare');
    }, [localSelectedParcels, selectedParcels, setSelectedParcels, getParcelKey, navigate, noteParcelEngagement]);

    // Check if a parcel is highlighted
    const isParcelHighlighted = useCallback((parcel) => {
        if (!highlightedParcelId) return false;
        const parcelKey = getParcelKey(parcel);
        return parcelKey === highlightedParcelId;
    }, [highlightedParcelId, getParcelKey]);

    // Handle parcel highlight from panel click
    const handleParcelHighlight = useCallback((parcel) => {
        const parcelId = getParcelKey(parcel);
        logger.log('Parcel highlighted from panel:', parcelId);
        updateHighlightedParcel(parcelId);
        
        // Pan map to the parcel
        if (mapRef && mapRef.current && mapRef.current.panToParcel) {
            mapRef.current.panToParcel(parcel);
        }
    }, [updateHighlightedParcel, getParcelKey, mapRef]);

    const getParcelScoreValue = useCallback((parcel) => {
        if (!parcel) return null;
        const rawScore =
            parcel.recommendation_score ??
            parcel.weighted_score ??
            parcel.score ??
            parcel?.land_parcel_data?.recommendation_score ??
            null;

        if (rawScore === null || rawScore === undefined || rawScore === '') {
            return null;
        }

        const numericScore = Number(rawScore);
        if (!Number.isNaN(numericScore)) {
            return numericScore.toFixed(3);
        }

        if (typeof rawScore === 'string') {
            return rawScore;
        }

        return null;
    }, []);

    useEffect(() => {
        setPanelPage(1);
        setPendingPanelPage(null);
    }, [searchFilters]);

    const sortedParcels = useMemo(() => {
        return [...parcels].sort((a, b) => b.recommendation_score - a.recommendation_score);
    }, [parcels]);

    const totalAvailableParcels = totalCount || sortedParcels.length;
    const totalPages = Math.max(1, Math.ceil((totalAvailableParcels || 0) / panelPageSize) || 1);
    const paginatedParcels = useMemo(() => {
        const startIndex = (panelPage - 1) * panelPageSize;
        return sortedParcels.slice(startIndex, startIndex + panelPageSize);
    }, [sortedParcels, panelPage, panelPageSize]);

    useEffect(() => {
        if (panelPage > totalPages) {
            setPanelPage(totalPages);
        }
    }, [panelPage, totalPages]);

    useEffect(() => {
        if (pendingPanelPage === null) return;
        const requiredCount = pendingPanelPage * panelPageSize;
        if (parcels.length >= requiredCount) {
            setPanelPage(pendingPanelPage);
            setPendingPanelPage(null);
            setIsPageLoading(false);
        } else if (!hasMoreData) {
            setPendingPanelPage(null);
            setIsPageLoading(false);
        }
    }, [parcels.length, pendingPanelPage, panelPageSize, hasMoreData]);

    const handlePageChange = useCallback(async (page) => {
        if (page < 1 || page > totalPages || page === panelPage) return;
        const requiredCount = page * panelPageSize;

        if (requiredCount > parcels.length && hasMoreData && onLoadMore) {
            try {
                setIsPageLoading(true);
                setPendingPanelPage(page);
                await onLoadMore();
            } catch (error) {
                logger.error('Error loading more parcels for pagination:', error);
                setPendingPanelPage(null);
                setIsPageLoading(false);
            }
        } else {
            setPanelPage(page);
            setPendingPanelPage(null);
        }
    }, [totalPages, panelPage, panelPageSize, parcels.length, hasMoreData, onLoadMore]);

    const visiblePageNumbers = useMemo(() => {
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, idx) => idx + 1);
        }

        // Do not show the last page; keep a compact window anchored near the start and current.
        if (panelPage <= 3) {
            return [1, 2, 3, 'ellipsis'];
        }

        // panelPage > 3
        return [1, 2, 'ellipsis', Math.max(3, panelPage - 1), panelPage];
    }, [panelPage, totalPages]);

    const handlePreviousPage = useCallback(() => {
        handlePageChange(panelPage - 1);
    }, [handlePageChange, panelPage]);

    const handleNextPage = useCallback(() => {
        handlePageChange(panelPage + 1);
    }, [handlePageChange, panelPage]);

    const handleFetchMorePages = useCallback(async () => {
        if (!onLoadMore || isPageLoading || isLoadingMore || isFiltering) return;
        try {
            setIsPageLoading(true);
            await onLoadMore();
        } catch (error) {
            logger.error('Error fetching additional parcels via ellipsis:', error);
        } finally {
            setIsPageLoading(false);
        }
    }, [onLoadMore, isPageLoading, isLoadingMore, isFiltering, hasMoreData]);

    const pageRangeStart = totalAvailableParcels === 0 ? 0 : Math.min((panelPage - 1) * panelPageSize + 1, totalAvailableParcels);
    const pageRangeEnd = totalAvailableParcels === 0
        ? 0
        : paginatedParcels.length === 0
            ? pageRangeStart
            : Math.min(pageRangeStart + paginatedParcels.length - 1, totalAvailableParcels);

    // Scroll to highlighted parcel when highlightedParcelId changes
    useEffect(() => {
        if (!highlightedParcelId) return;

        // Use the ref to get the actual DOM element
        const parcelElement = parcelRefs.current[highlightedParcelId];
        if (parcelElement) {
            // Use scrollIntoView for more accurate scrolling
            parcelElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center', // Center the element in the viewport
                inline: 'nearest'
            });
            logger.log('Scrolled to highlighted parcel:', highlightedParcelId);
        } else {
            logger.log('Could not find highlighted parcel in refs:', highlightedParcelId);
        }
    }, [highlightedParcelId]);

    const [weights, setWeights] = useState({
        "disadvantaged_community": 2,
        "scaqmd_jurisdiction": 2,
        "warehouse_proximity": 2,
        "intermodal_proximity": 2,
        "service_center_proximity": 2,
        "heatmap_intensity": 2,
        "mdhd_proximity": 2,
        "land_value": 2,
        "area": 2,
        "circuit_capacity": 2,
    });

    // Function to handle weight changes
    const handleWeightChange = (key, value) => {
        setWeights(prev => ({
            ...prev,
            [key]: parseFloat(value)
        }));
    };

    // Function to reset weights to default
    const resetWeights = () => {
        setWeights({
            "disadvantaged_community": 2,
            "scaqmd_jurisdiction": 2,
            "warehouse_proximity": 2,
            "intermodal_proximity": 2,
            "service_center_proximity": 2,
            "heatmap_intensity": 2,
            "mdhd_proximity": 2,
            "land_value": 2,
            "area": 2,
            "circuit_capacity": 2,
        });
    };

    // Helper function to get importance label from numeric value
    const getImportanceLabel = (value) => {
        switch(value) {
            case 0: return "Not Important";
            case 1: return "Low Priority";
            case 2: return "Neutral";
            case 3: return "High Priority";
            case 4: return "Very Important";
            default: return "Neutral";
        }
    };

    // Helper function to get slider color based on importance
    const getSliderColor = (value) => {
        switch(value) {
            case 0: return "#9CA3AF"; // gray-400
            case 1: return "#60A5FA"; // blue-400
            case 2: return "#34D399"; // emerald-400
            case 3: return "#FBBF24"; // amber-400
            case 4: return "#F87171"; // red-400
            default: return "#34D399";
        }
    };

    // Format weight labels for display
    const formatWeightLabel = (key) => {
        // Based on the key, return the label. Keys are pulled from weight definition.
        switch(key) {
            case "disadvantaged_community": return "The parcel is in a disadvantaged community";
            case "scaqmd_jurisdiction": return "The parcel lies within SCAQMD Jurisdiction";
            case "warehouse_proximity": return "The parcel is close to a warehouse";
            case "intermodal_proximity": return "The parcel is close to an intermodal terminal";
            case "service_center_proximity": return "The parcel is close to a service center";
            case "heatmap_intensity": return "The area around the parcel has frequent truck stops";
            case "mdhd_proximity": return "The parcel is further from existing chargers";
            case "land_value": return "The parcel has a lower land value";
            case "area": return "The parcel has large area";
            case "circuit_capacity": return "The parcel is near a circuit with high hosting capacity";
            default: return key.split('_').map(word =>
                word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
        }
    };

    const handleApplyPreferences = async () => {
        logger.log("Sending weighted preference search request")
        setPreferenceSearchLoading(true);
        try {
            // Create query parameters with each weight as a separate parameter
            const params = {};
            Object.keys(weights).forEach(key => {
                params[`weights[${key}]`] = weights[key];
            });
            
            const response = await axios.get(API_ENDPOINTS.getLandParcelsWeighted, {
                params: params,
                headers: { 
                    Authorization: `Token ${localStorage.getItem('authToken')}` 
                }
            });
            
            logger.log(`Received ${response.data.length} weighted parcels`);
            
            // Transform the response data to match the expected parcel structure
            const transformedParcels = response.data.map(item => ({
                ...item.land_parcel_data,
                weighted_score: item.weighted_score
            }));
            
            // Update the parcels through the onSearch callback with the weighted results
            // This will integrate with the existing pagination and display system
            onSearch(searchFilters, transformedParcels);
            
            setNotification({
                message: `Found ${transformedParcels.length} parcels matching your preferences`,
                type: "success"
            });
            setShowWeightedSearch(false);
        } catch (error) {
            logger.error("Error applying preferences:", error);
            setNotification({
                message: "Failed to apply preferences",
                type: "error"
            });
            setShowWeightedSearch(false);
        } finally {
            setPreferenceSearchLoading(false);
        }
    }

    const WeightedSearchWindow = () => {
        // Split weights into two columns
        const weightEntries = Object.entries(weights);
        const midpoint = Math.ceil(weightEntries.length / 2);
        const leftColumn = weightEntries.slice(0, midpoint);
        const rightColumn = weightEntries.slice(midpoint);

        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowWeightedSearch(false)}
            >
                <style>{sliderStyles}</style>
                <div
                    className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b">
                        <h2 className="text-xl font-semibold text-gray-800">Search Preferences</h2>
                        <button
                            onClick={() => setShowWeightedSearch(false)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <PiXBold className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <p className="text-sm text-gray-600 mb-6">
                            Adjust the importance of each factor in the parcel recommendation algorithm. Use the scale from "Not Important" to "Very Important" to prioritize different criteria.
                        </p>

                        {/* Weight Sliders - Two Column Layout */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            {/* Left Column */}
                            <div className="space-y-6">
                                {leftColumn.map(([key, value]) => (
                                    <div key={key} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-gray-700">
                                                {formatWeightLabel(key)}
                                            </label>
                                            <span className="text-xs font-medium px-2 py-1 rounded" 
                                                  style={{ 
                                                      backgroundColor: `${getSliderColor(value)}20`,
                                                      color: getSliderColor(value)
                                                  }}>
                                                {getImportanceLabel(value)}
                                            </span>
                                        </div>
                                        <div className="relative">
                                            <input
                                                type="range"
                                                min="0"
                                                max="4"
                                                step="1"
                                                value={value}
                                                onChange={(e) => handleWeightChange(key, e.target.value)}
                                                className="w-full weight-slider"
                                                style={{
                                                    background: `linear-gradient(to right, ${getSliderColor(value)} 0%, ${getSliderColor(value)} ${((value) / 4) * 100}%, #E5E7EB ${((value) / 4) * 100}%, #E5E7EB 100%)`
                                                }}
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1 px-1">
                                                <span>Not Important</span>
                                                <span>Low Priority</span>
                                                <span>Neutral</span>
                                                <span>High Priority</span>
                                                <span>Very Important</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Right Column */}
                            <div className="space-y-6">
                                {rightColumn.map(([key, value]) => (
                                    <div key={key} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-gray-700">
                                                {formatWeightLabel(key)}
                                            </label>
                                            <span className="text-xs font-medium px-2 py-1 rounded" 
                                                  style={{ 
                                                      backgroundColor: `${getSliderColor(value)}20`,
                                                      color: getSliderColor(value)
                                                  }}>
                                                {getImportanceLabel(value)}
                                            </span>
                                        </div>
                                        <div className="relative">
                                            <input
                                                type="range"
                                                min="0"
                                                max="4"
                                                step="1"
                                                value={value}
                                                onChange={(e) => handleWeightChange(key, e.target.value)}
                                                className="w-full weight-slider"
                                                style={{
                                                    background: `linear-gradient(to right, ${getSliderColor(value)} 0%, ${getSliderColor(value)} ${((value) / 4) * 100}%, #E5E7EB ${((value) / 4) * 100}%, #E5E7EB 100%)`
                                                }}
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1 px-1">
                                                <span>Not Important</span>
                                                <span>Low Priority</span>
                                                <span>Neutral</span>
                                                <span>High Priority</span>
                                                <span>Very Important</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-8">
                            <button
                                onClick={resetWeights}
                                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                            >
                                Reset to Default
                            </button>
                            <button
                                onClick={handleApplyPreferences}
                                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                            >
                                Apply Preferences & Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const FiltersWindow = () => {
        const [localFilters, setLocalFilters] = useState(searchFilters);

        const handleFilterChange = (key, value) => {
            setLocalFilters(prev => ({
                ...prev,
                [key]: value
            }));
        };

        const handleApplyFilters = () => {
            onSearch(localFilters);
            setShowFilters(false);
        };

        const handleClearFilters = () => {
            const clearedFilters = {
                zipCode: '',
                city: '',
                minLandValue: '',
                maxLandValue: '',
                minArea: '',
                maxArea: '',
                inScaqmdJurisdiction: false,
                inDisadvantagedCommunity: false,
            };
            setLocalFilters(clearedFilters);
            onSearch(clearedFilters);
            setShowFilters(false);
        };

        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowFilters(false)}
            >
                <div
                    className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[70vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b">
                        <h2 className="text-xl font-semibold text-gray-800">Search Filters</h2>
                        <button
                            onClick={() => setShowFilters(false)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <PiXBold className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <p className="text-sm text-gray-600 mb-6">
                            Filter parcels by location, land value, and area to find the most suitable properties.
                        </p>

                        {/* Location Filters */}
                        <div className="mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        ZIP Code
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., 90210"
                                        value={localFilters.zipCode}
                                        onChange={(e) => handleFilterChange('zipCode', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Los Angeles"
                                        value={localFilters.city}
                                        onChange={(e) => handleFilterChange('city', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Land Value Filters */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Land value ($)
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <input
                                        type="number"
                                        placeholder="Enter minimum land value"
                                        value={localFilters.minLandValue}
                                        onChange={(e) => handleFilterChange('minLandValue', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="number"
                                        placeholder="Enter maximum land value"
                                        value={localFilters.maxLandValue}
                                        onChange={(e) => handleFilterChange('maxLandValue', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Area Filters */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Area (sq ft)
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>

                                    <input
                                        type="number"
                                        placeholder="Enter minimum area"
                                        value={localFilters.minArea}
                                        onChange={(e) => handleFilterChange('minArea', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <input
                                        type="number"
                                        placeholder="Enter maximum area"
                                        value={localFilters.maxArea}
                                        onChange={(e) => handleFilterChange('maxArea', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* TODO: Add back in once the backend is more optimized for the filters*/}
                        {/* <div className="mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={localFilters.inScaqmdJurisdiction}
                                        onChange={(e) => handleFilterChange('inScaqmdJurisdiction', e.target.checked)}
                                    />
                                    <label className="block text-sm font-medium text-gray-700">
                                        In SCAQMD Jurisdiction?
                                    </label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={localFilters.inDisadvantagedCommunity}
                                        onChange={(e) => handleFilterChange('inDisadvantagedCommunity', e.target.checked)}
                                    />
                                    <label className="block text-sm font-medium text-gray-700">
                                        In an overburdened community?
                                    </label>
                                </div>
                            </div>
                        </div> */}

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-8">
                            <button
                                onClick={handleClearFilters}
                                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                            >
                                Clear All Filters
                            </button>
                            <button
                                onClick={handleApplyFilters}
                                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full">
            {preferenceSearchLoading && <LoadingSpinner message="Searching for parcels with your preferences..." />}
            {showWeightedSearch && <WeightedSearchWindow />}
            {showFilters && <FiltersWindow />}
            {notification && <Notification notification={notification} />}
            {/* Search Filters */}
            <SearchFilters
                onSearch={onSearch}
                searchFilters={searchFilters}
                isFiltering={isFiltering}
                setShowWeightedSearch={setShowWeightedSearch}
                setShowFilters={setShowFilters}
                onShowDataLayers={dataLayerGroups.length ? () => setIsDataLayersOpen(true) : undefined}
                dataLayerCount={dataLayerCount}
            />

            {/* Results Summary */}
            {/* {!isFiltering && parcels.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm text-gray-600">
                    Showing {parcels.length} of {parcels.length} parcels
                    {hasMoreData && (
                        <span className="text-blue-600 ml-2">• Scroll or click to load more</span>
                    )}
                </div>
            )} */}

            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <div className="flex flex-col">
                    {totalAvailableParcels > 0 && (
                        <div className="flex flex-col text-xs text-gray-400 space-y-1">
                            <span>
                                {totalAvailableParcels.toLocaleString()} parcels within map area
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {/* <span className="text-gray-600">Selected: {localSelectedParcels.length}</span> */}
                    <span className="text-gray-600">In table: {selectedParcels?.length ?? 0}</span>
                    {localSelectedParcels.length > 0 && (
                        <>
                            <button
                                onClick={handleAddToTable}
                                className="px-3 py-1 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors"
                            >
                                Add to Table
                            </button>
                            <button
                                onClick={clearLocalSelection}
                                className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-50"
                            >
                                Clear Selected
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Scrollable Parcel List */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-2" style={{ scrollBehavior: 'smooth' }}>
                {isFiltering && (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">Searching...</span>
                    </div>
                )}

                {!isFiltering && paginatedParcels.length === 0 && !loading && !isPageLoading && (
                    <div className="text-center py-8 text-gray-500">
                        <p>No land parcels found in current map view.</p>
                        <p className="text-sm mt-1">Try panning or zooming the map to see parcels in different areas.</p>
                    </div>
                )}

                {!isFiltering && paginatedParcels.map((parcel) => {
                    if (!parcel) return null;
                    const parcelKey = getParcelKey(parcel);
                            const alreadyInTable = isParcelSelected(parcel);
                            return (
                        <LandParcelCard
                            key={parcelKey}
                            ref={(el) => {
                                if (el) {
                                    parcelRefs.current[parcelKey] = el;
                                } else {
                                    delete parcelRefs.current[parcelKey];
                                }
                            }}
                            parcel={parcel}
                            onToggle={toggleLocalParcelSelection}
                            selected={isLocallySelected(parcel)}
                            alreadyInTable={alreadyInTable}
                            highlighted={isParcelHighlighted(parcel)}
                            onHighlight={handleParcelHighlight}
                            score={getParcelScoreValue(parcel)}

                        />
                    );
                })}

                {isPageLoading && (
                    <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">Loading page...</span>
                    </div>
                )}

                {/* Loading indicator for infinite scroll */}
                {isLoadingMore && (
                    <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">Loading more parcels...</span>
                    </div>
                )}
                {loading && !isLoadingMore && paginatedParcels.length === 0 && <SmallLoadingSpinner />}
            </div>

            {totalPages > 1 && (
                <div className="border-t pt-3 mt-3 flex items-center justify-between">
                    <button
                        onClick={handlePreviousPage}
                        disabled={panelPage === 1 || isPageLoading || isFiltering}
                        className="px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-700 disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <div className="flex items-center gap-1">
                        {visiblePageNumbers.map((page, index) => {
                            if (page === 'ellipsis') {
                                return <span key={`ellipsis-${index}`} className="px-2 text-gray-500">…</span>;
                            }

                            return (
                                <button
                                    key={`page-${page}`}
                                    onClick={() => handlePageChange(page)}
                                    disabled={isPageLoading || isFiltering}
                                    className={`px-3 py-1 text-sm rounded-md border ${
                                        page === panelPage
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    {page}
                                </button>
                            );
                        })}
                {/* {hasMoreData && (
                    <button
                        onClick={handleFetchMorePages}
                        disabled={isPageLoading || isLoadingMore || isFiltering}
                        className="px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        title="Load more parcels"
                    >
                        …
                    </button>
                )} */}
                    </div>
                    <button
                        onClick={() => {
                            if (panelPage < totalPages) {
                                handleNextPage();
                            } else {
                                handleFetchMorePages();
                            }
                        }}
                        disabled={isPageLoading || isFiltering || isLoadingMore}
                        className="px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-700 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}
            <DataLayersDrawer
                isOpen={isDataLayersOpen}
                onClose={() => setIsDataLayersOpen(false)}
                groups={dataLayerGroups}
            />
        </div>
    );
};